import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { dealStore } from '@/models/stores/deals';
import type { Deal, FileMetadata, Payment } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useCallback, useId } from 'react';

const PaymentUploadReceiptButton = ({
  paymentId,
  dealId,
  requiresAlert,
}: {
  paymentId: Payment['id'];
  dealId: Deal['id'];
  requiresAlert: boolean;
}) => {
  const queryClient = useQueryClient();
  const inputId = useId();

  const uploadFilesMutation = useMutation({
    onMutate: async () =>
      // files: FileList
      {
        // optimistically update the files in the deals store with optimistic ID's and optimistic urls
        dealStore.uploadReceiptToPayment(paymentId);
      },
    onSuccess: async () =>
      // files: FileMetadata[]
      {
        // update the files in the deals store
        // dealStore.addFiles({
        //   dealId,
        //   files,
        // });
      },
    mutationFn: async (files: FileList): Promise<FileMetadata[]> => {
      const formData = new FormData();
      formData.append('file', files[0]);

      const response = await pdkAxios.post(
        // /:payment_id/add-receipt
        `/deals/${dealId}/payments/${paymentId}/add-receipt`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return response.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['deals', dealId],
      });
    },
  });

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        uploadFilesMutation.mutate(e.target.files);
      }
    },
    [uploadFilesMutation],
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex flex-col items-center justify-center space-y-2">
          <p className="text-xs  text-gray-600">
            <label htmlFor={inputId} className="hover:underline cursor-pointer">
              <Upload
                className={cn('h-3.5 w-3.5 text-gray-500 cursor-pointer', {
                  'text-red-500': requiresAlert,
                })}
              />
            </label>
          </p>
          <input
            id={inputId}
            type="file"
            className="hidden"
            // multiple
            onChange={handleFileInputChange}
          />
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-row gap-2">
          <p className="text-xs">Attach an image or file to this payment</p>
          <KeyboardKeyIcon className="w-4 h-4">
            {HOTKEYS.DEAL_UPLOAD_FILE}
          </KeyboardKeyIcon>
        </div>

        <p className="text-xs text-gray-500">
          Supported file type: PNG, JPEG, PDF, CSV, TXT
        </p>
      </TooltipContent>
    </Tooltip>
  );
};

export default PaymentUploadReceiptButton;
